import { CloseOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { useState } from 'react';
import { Optional } from '../../Shared';
import CollapsibleGroup from './CollapsibleGroup';
import type { GroupWithOptions, Option } from './types';

type AccessibleSelectProps = {
  readonly onSearchTeamRoles: (value: string) => void;
  readonly onChange?: (value: string) => void;
  readonly value?: Option;
  readonly showEmptyOption?: boolean;
  readonly options: readonly GroupWithOptions[];
  readonly className: string;
  readonly id: string;
  readonly title: string;
  readonly height?: string;
};

/**
 * A component that displays groups of options, where each option is a button that can be selected.
 * This custom component has been created for simplicity to comply with accessibility requirements
 * since standard AntD Tree Select components do not provide aria attributes that conform with acceptable values
 */
const GroupSelect = ({
  id,
  className,
  value,
  options,
  onSearchTeamRoles,
  showEmptyOption,
  onChange,
}: AccessibleSelectProps) => {
  const [input, setInput] = useState<string | undefined>(value?.value);

  const [optionsHidden, setOptionsHidden] = useState<boolean>(true);

  const hasResults = options.filter((opt) => opt.options.length > 0).length > 0;

  return (
    <div className={className}>
      <input
        id={id}
        className={`${className}-input`}
        type="search"
        onChange={(e) => {
          setInput(e.currentTarget.value);
          onSearchTeamRoles(e.currentTarget.value);
        }}
        value={input}
        style={{
          width: '100%',
          border: '1px solid lightgrey',
          height: '40px',
          padding: '10px',
        }}
        onFocus={() => setOptionsHidden(true)}
        onSelect={() => setOptionsHidden(false)}
        aria-label="group-input-search-box"
      ></input>

      <div
        style={{
          maxHeight: '300px',
          overflow: 'auto',
          border: '1px solid lightgrey',
          position: 'absolute',
          zIndex: 1,
          background: 'white',
          padding: '10px',
        }}
        id="select-options"
        hidden={optionsHidden}
      >
        <Optional show={!hasResults}>No Results</Optional>
        <Optional show={hasResults}>
          <Button
            type="text"
            style={{
              width: '20px',
              height: '20px',
              padding: 0,
              marginRight: '20px',
              position: 'absolute',
              right: 0,
              zIndex: 1,
            }}
            onClick={() => setOptionsHidden(true)}
          >
            <CloseOutlined />
          </Button>
          {options
            .filter((opt) => opt.options.length > 0 || showEmptyOption === true)
            .map((opt, i) => (
              <CollapsibleGroup
                key={i}
                groupWithOptions={opt}
                onChange={(selected) => {
                  const selectedValue = options
                    .find((v) => v.options.some((o) => o.id === selected))
                    ?.options.find((o) => o.id === selected);
                  if (selectedValue !== undefined) {
                    setInput(selectedValue.value);
                  }
                  if (onChange !== undefined) {
                    onChange(selected);
                  }
                  setOptionsHidden(true);
                }}
              />
            ))}
        </Optional>
      </div>
    </div>
  );
};

export default GroupSelect;
